// Web twin of composeApp's DisplayStyle.kt.

package com.islamicmedia.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.remember
import androidx.compose.runtime.compositionLocalOf
import com.islamicmedia.shared.domain.model.AnalysesLayout
import com.islamicmedia.shared.domain.model.DisplaySettings
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.presentation.AnalysisStripEntry
import com.islamicmedia.shared.presentation.HistoryItem
import com.islamicmedia.shared.presentation.TaskStatus
import com.islamicmedia.site.theme.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import kotlin.math.roundToInt

/**
 * Body-prose color override for analysis text. Null = use the caller's own default gray. Read by
 * [FormattedText]/[MarkdownBlocks] so the color slider darkens the readable prose toward black.
 */
val LocalBodyTextColor = compositionLocalOf<CSSColorValue?> { null }

/**
 * Applies the user's Display style to [content]:
 *  - TEXT SIZE via CSS `zoom` on a wrapper (fixed-px text won't cascade a container font-size; zoom
 *    scales text + layout proportionally and reflows to fit).
 *  - BODY COLOR via [LocalBodyTextColor], interpolated gray → black by the color slider.
 */
@Composable
fun DisplayStyled(settings: DisplaySettings, content: @Composable () -> Unit) {
    val scale = settings.fontScale
    CompositionLocalProvider(LocalBodyTextColor provides webBodyColor(settings)) {
        Div(attrs = {
            style { if (scale != 1.0f) property("zoom", scale.toString()) }
        }) { content() }
    }
}

/** Interpolated body color: null at darkness 0 (keep caller's gray), else Gray600 → Gray900. */
private fun webBodyColor(settings: DisplaySettings): CSSColorValue? {
    val d = settings.darkness
    if (d <= 0.001f) return null
    // Both theme grays are neutral (equal channels): Gray600 = 82, Gray900 = 23.
    val v = (82 + (23 - 82) * d).roundToInt()
    return rgb(v, v, v)
}

/**
 * The Settings screen's two independent sections, each ending in its own live preview:
 *
 *  1. YOUR ANALYSES — the list/2-col/3-col picker, previewed on real cards.
 *  2. ANALYSIS TEXT — size + colour sliders, previewed on a real analysis snippet.
 *
 * The rule that matters here: a preview belongs directly under the controls it previews. The
 * layout picker briefly sat between the text sliders and their preview card, which made that card
 * look like it was previewing the layout choice — it wasn't, and nothing about it moved when you
 * changed one. The divider between the sections exists to make that boundary impossible to
 * misread. Mirrors composeApp's twin.
 */
@Composable
fun DisplayStyleSettings(
    settings: DisplaySettings,
    onChange: (DisplaySettings) -> Unit,
    /** The user's own analyses, newest first — the layout preview draws from these so it
     *  shows their real covers rather than stand-ins. Empty is fine; see the preview. */
    history: List<HistoryItem> = emptyList(),
) {
    Div(attrs = { style { width(100.percent); property("max-width", "480px"); property("margin", "0 auto") } }) {
        // ── Section 1: the analyses list ──────────────────────────────────────────────────────
        SettingSectionLabel("Your analyses", topMargin = 0)
        LayoutPicker(selected = settings.analysesLayout) { onChange(settings.copy(analysesColumns = it.columns)) }
        SettingSectionLabel("Preview")
        AnalysesLayoutPreview(settings.analysesLayout, history)

        // ── Section 2: analysis text ──────────────────────────────────────────────────────────
        Div(attrs = {
            style {
                height(1.px); backgroundColor(Gray200)
                property("margin", "24px 0 20px")
            }
        })
        SettingSectionLabel("Analysis text", topMargin = 0)
        SliderRow("Text size", scaleToPercent(settings.textScale)) { p ->
            onChange(settings.copy(textScale = percentToScale(p)))
        }
        SliderRow("Text color", (settings.darkness * 100f).roundToInt()) { p ->
            onChange(settings.copy(colorDarkness = p / 100f))
        }
        SettingSectionLabel("Preview")
        DisplayStyled(settings) { DisplayStylePreviewCard() }
    }
}

/** The small uppercase heading above each control group. One definition so the two sections can't
 *  drift apart on spacing. */
@Composable
private fun SettingSectionLabel(text: String, topMargin: Int = 16) {
    Div(attrs = {
        style {
            fontSize(12.px); fontWeight(700); color(Gray500)
            property("text-transform", "uppercase"); property("letter-spacing", "0.05em")
            property("margin", "${topMargin}px 0 10px")
        }
    }) { Text(text) }
}

/**
 * Three entries rendered through the REAL card composable, not a mock-up.
 *
 * Drawing a schematic instead would have been less code and a lie the first time either card
 * changed — this one cannot drift, because it goes through the same [AnalysisCardGrid] the list
 * itself uses.
 *
 * It draws from the user's OWN analyses, so the covers in the preview are the covers they are
 * choosing a layout for. Their titles are also the honest test of the layout: a preview of three
 * short invented names would never show what happens to the long one they actually have.
 *
 * Entries WITH artwork come first, then anything else, then invented ones to make up the numbers.
 * That order matters for a new user with nothing analysed yet — they still see three cards rather
 * than an empty box, just with the media-type fallback art instead of covers.
 *
 * Three, not two, so the 3-column option has a full row to show and the 2-column option shows what
 * a ragged last row looks like. Mirrors composeApp's twin.
 */
@Composable
private fun AnalysesLayoutPreview(layout: AnalysesLayout, history: List<HistoryItem>) {
    val samples = remember(history) {
        // Inert on purpose: this is a picture of the list, not the list. Clicking a preview card
        // should do nothing rather than navigate out of Settings.
        val fromHistory = history
            .sortedWith(compareByDescending<HistoryItem> { it.result.coverUrl != null }.thenByDescending { it.timestamp })
            .take(3)
            .map { it.toStripEntry(onClick = {}) }

        val filler = listOf(
            Triple("Bluey (2018)", MediaType.TV_SHOWS, 9),
            Triple("The Hobbit (1937)", MediaType.BOOKS, 6),
            Triple("Minecraft (2011)", MediaType.GAMES, 8),
        ).mapIndexed { index, (title, type, score) ->
            AnalysisStripEntry(
                id = "preview-$index",
                title = title,
                mediaType = type,
                status = TaskStatus.COMPLETE,
                progressFraction = 1f,
                score = score,
                sortKey = index.toLong(),
                onClick = {},
                onCancel = null,
            )
        }
        (fromHistory + filler).take(3)
    }
    AnalysisCardGrid(entries = samples, layout = layout)
}

/**
 * List / 2 columns / 3 columns, as a segmented track.
 *
 * Deliberately not a slider like the two above it: those map a continuous value, this is three
 * named states, and a three-stop slider gives no clue what the stops are until you land on one.
 * The track styling mirrors the filter pills over the analyses grid itself, so the control looks
 * like the thing it changes.
 */
@Composable
private fun LayoutPicker(selected: AnalysesLayout, onSelect: (AnalysesLayout) -> Unit) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            width(100.percent)
            gap(2.px)
            borderRadius(999.px)
            backgroundColor(Gray100)
            padding(2.px)
        }
    }) {
        AnalysesLayout.entries.forEach { option ->
            val isSelected = option == selected
            Div(attrs = {
                onClick { onSelect(option) }
                style {
                    property("flex", "1")
                    property("text-align", "center")
                    padding(8.px, 4.px)
                    borderRadius(999.px)
                    cursor("pointer")
                    fontSize(12.px)
                    fontWeight(if (isSelected) 700 else 500)
                    backgroundColor(if (isSelected) Primary else Color.transparent)
                    color(if (isSelected) Color.white else Gray500)
                }
            }) { Text(option.label) }
        }
    }
}

@Composable
private fun SliderRow(label: String, valuePercent: Int, onChange: (Int) -> Unit) {
    Div(attrs = {
        style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(14.px); property("margin-bottom", "14px") }
    }) {
        Span(attrs = {
            style { property("flex", "0 0 84px"); fontSize(14.px); fontWeight(500); color(Gray700) }
        }) { Text(label) }
        RangeInput(value = valuePercent, min = 0, max = 100, attrs = {
            onInput { ev ->
                (ev.target as? org.w3c.dom.HTMLInputElement)?.value?.toDoubleOrNull()?.let { onChange(it.roundToInt()) }
            }
            style {
                property("flex", "1")
                property("accent-color", Primary.toString())
                cursor("pointer")
            }
        })
    }
}

private fun scaleToPercent(scale: Float): Int =
    (((scale - DisplaySettings.MIN_SCALE) / (DisplaySettings.MAX_SCALE - DisplaySettings.MIN_SCALE)) * 100f)
        .roundToInt().coerceIn(0, 100)

private fun percentToScale(p: Int): Float =
    DisplaySettings.MIN_SCALE + (p / 100f) * (DisplaySettings.MAX_SCALE - DisplaySettings.MIN_SCALE)

/** A small but realistic-looking analysis snippet — verdict + one concern — used only in the preview.
 *  Renders the SAME [FormattedText] prose real results use, so the size/color settings preview exactly
 *  how a real analysis will look. */
@Composable
private fun DisplayStylePreviewCard() {
    Div(attrs = {
        style {
            backgroundColor(ScoreAmberBg)
            property("border", "1px solid rgba(245,158,11,0.35)")
            borderRadius(16.px); padding(14.px)
        }
    }) {
        Div(attrs = { style { fontSize(11.px); fontWeight(700); color(ScoreAmber); property("letter-spacing", "0.08em") } }) { Text("USE CAUTION") }
        Div(attrs = { style { fontSize(16.px); fontWeight(700); color(Gray900); property("margin", "4px 0") } }) { Text("A few concerns for younger kids") }
        FormattedText(
            text = "Wholesome themes overall, with some mild action and language to be aware of.",
            color = Gray600,
            fontSize = 14.px
        )
        Div(attrs = { style { height(1.px); property("background", "rgba(245,158,11,0.35)"); property("margin", "12px 0") } })
        Div(attrs = { style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); property("margin-bottom", "6px") } }) {
            Div(attrs = { style { fontSize(15.px); fontWeight(600); color(Gray900); property("flex", "1") } }) { Text("Islamic Concern") }
            Span(attrs = {
                style { fontSize(11.px); fontWeight(700); color(ScoreAmber); backgroundColor(Color("rgba(255,255,255,0.6)")); borderRadius(6.px); padding(3.px, 8.px) }
            }) { Text("Medium") }
        }
        FormattedText(
            text = "Some action scenes with mild peril; no graphic injury is shown on screen.",
            color = Gray600,
            fontSize = 14.px
        )
        Div(attrs = { style { fontSize(13.px); fontWeight(700); color(Primary); property("margin", "10px 0 2px") } }) { Text("Islamic Perspective") }
        FormattedText(
            text = "Islam encourages courage while discouraging needless harm to others.",
            color = Gray600,
            fontSize = 13.px
        )
    }
}
